import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Save, Upload, Loader2, Check, Plus, MapPin, Users, Globe, Calendar, Trash2, RefreshCw, Search, ArrowRight, Heart, Eye, X, Instagram, Linkedin, Plug, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import TikTokIcon from '../components/TikTokIcon';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../contexts/UserProfileContext';
import { useLanguage } from '../contexts/LanguageContext';
import { showToast } from '../utils/errorHandler';
import {
  CEVEE_TAGS_POOL, SIZE_OPTIONS,
} from '../constants/companyOptions';
import { translateTag, CEVEE_TAGS_EN } from '../constants/tagTranslations';
import CompanyCard from '../components/CompanyCard';
import RecruiterLoggedOutGate from '../components/RecruiterLoggedOutGate';
import { RecruiterPageShell } from '../components/Recruiter/RecruiterStates';
import { RECRUITER_DEMO_URL } from '../constants/recruiterContact';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

function shuffleAndPick<T>(arr: T[], n: number): T[] {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
}

interface CompanyForm {
  name: string;
  bio: string;
  industry: string;
  size: string;
  location: string;
  website: string;
  cevee_tags: string[];
  looking_for: string;
  founded_year: string;
  instagram_url: string;
  tiktok_url: string;
  linkedin_url: string;
}

const emptyForm: CompanyForm = {
  name: '', bio: '', industry: '', size: '', location: '', website: '',
  cevee_tags: [], looking_for: '', founded_year: '',
  instagram_url: '', tiktok_url: '', linkedin_url: '',
};

export default function CompanyManage() {
  const { t } = useTranslation(['companyManage', 'jobRadarAi']);
  const { language } = useLanguage();
  const { apiCall, user, isSignedIn, isLoading: authLoading } = useAuth();
  const { userProfile } = useUserProfile();
  const isPremiumRecruiter =
    userProfile?.subscription_type === 'recruiter_premium' ||
    userProfile?.subscription_type === 'recruiter_star';
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [form, setForm] = useState<CompanyForm>(emptyForm);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ceveeTagsPreview, setCeveeTagsPreview] = useState(() => shuffleAndPick(CEVEE_TAGS_POOL, 10));
  const [customVibeInput, setCustomVibeInput] = useState('');
  const [pendingLogo, setPendingLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [canManageCompany, setCanManageCompany] = useState(false);
  const [companyRole, setCompanyRole] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<{ id: string; email?: string; name: string; company_role: string }[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [mode, setMode] = useState<'choose' | 'create' | 'join'>('choose');
  const [previewBioExpanded, setPreviewBioExpanded] = useState(false);
  const [previewLookingForExpanded, setPreviewLookingForExpanded] = useState(false);
  const [showExampleModal, setShowExampleModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [joining, setJoining] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ATS Integration state
  const [atsExpanded, setAtsExpanded] = useState(true);
  const [atsProvider, setAtsProvider] = useState('ashby');
  const [atsToken, setAtsToken] = useState('');
  const [atsPersonioCompanyId, setAtsPersonioCompanyId] = useState('');
  const [atsPersonioSubdomain, setAtsPersonioSubdomain] = useState('');
  const [atsConnecting, setAtsConnecting] = useState(false);
  const [atsConnections, setAtsConnections] = useState<any[]>([]);
  const [atsJobs, setAtsJobs] = useState<any[]>([]);
  const [atsSyncing, setAtsSyncing] = useState(false);
  const [atsLoadingConnections, setAtsLoadingConnections] = useState(false);

  const searchCompanies = useCallback(async (q: string) => {
    if (q.trim().length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/companies/search-for-join?q=${encodeURIComponent(q)}`);
      if (resp.ok) {
        const data = await resp.json();
        setSearchResults(data.companies || []);
      }
    } catch {} finally { setSearching(false); }
  }, [apiCall]);

  const handleSearchInput = (val: string) => {
    setSearchQuery(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchCompanies(val), 300);
  };

  const handleLeave = async () => {
    setLeaving(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/companies/leave`, { method: 'POST' });
      if (resp.ok) {
        setCompanyId(null);
        setForm(emptyForm);
        setLogoUrl(null);
        setIsOwner(true);
        setCanManageCompany(true);
        setCompanyRole(null);
        setMode('choose');
        showToast(t('toast.left'), 'success');
      } else {
        showToast(t('toast.leave_failed'), 'error');
      }
    } catch { showToast(t('toast.leave_failed'), 'error'); }
    finally { setLeaving(false); }
  };

  const handleJoin = async (id: string) => {
    setJoining(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/companies/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: id }),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.success && data.company) {
          const c = data.company;
          setCompanyId(c.id);
          setIsActive(c.is_active !== false);
          setLogoUrl(c.logo_url);
          setForm({
            name: c.name || '', bio: c.bio || '', industry: c.industry || '',
            size: c.size || '', location: c.location || '', website: c.website || '',
            cevee_tags: c.cevee_tags || [], looking_for: c.looking_for || '',
            founded_year: c.founded_year?.toString() || '',
            instagram_url: c.instagram_url || '', tiktok_url: c.tiktok_url || '',
            linkedin_url: c.linkedin_url || '',
          });
          showToast(t('toast.joined'), 'success');
          navigate('/?tab=hire');
          return;
        }
      } else {
        showToast(t('toast.join_failed'), 'error');
      }
    } catch { showToast(t('toast.join_failed'), 'error'); }
    finally { setJoining(false); }
  };

  // ── Load existing company ─────────────────────────────────────

  const didLoad = useRef(false);
  useEffect(() => {
    if (!user || didLoad.current) return;
    didLoad.current = true;
    (async () => {
      try {
        const resp = await apiCall(`${API_BASE}/api/v1/companies/my-company`);
        if (!resp.ok) { setLoading(false); return; }
        const data = await resp.json();
        if (data.company) {
          const c = data.company;
          setCompanyId(c.id);
          setIsActive(c.is_active !== false);
          setLogoUrl(c.logo_url);
          setIsOwner(c.is_owner === true);
          const can =
            c.can_manage_company === true ||
            (c.can_manage_company == null && c.is_owner === true);
          setCanManageCompany(!!can);
          setCompanyRole(typeof c.company_role === 'string' ? c.company_role : null);
          setForm({
            name: c.name || '',
            bio: c.bio || '',
            industry: c.industry || '',
            size: c.size || '',
            location: c.location || '',
            website: c.website || '',
            cevee_tags: c.cevee_tags || [],
            looking_for: c.looking_for || '',
            founded_year: c.founded_year?.toString() || '',
            instagram_url: c.instagram_url || '',
            tiktok_url: c.tiktok_url || '',
            linkedin_url: c.linkedin_url || '',
          });
        }
      } catch (e) {
        console.error('Failed to load company:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── ATS helpers ───────────────────────────────────────────────

  const loadAtsConnections = useCallback(async (cId: string) => {
    setAtsLoadingConnections(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/ats/connections/${cId}`);
      if (resp.ok) {
        const data = await resp.json();
        setAtsConnections(data.connections || []);
      }
    } catch {} finally { setAtsLoadingConnections(false); }
  }, [apiCall]);

  const loadAtsJobs = useCallback(async (cId: string) => {
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/ats/jobs/${cId}`);
      if (resp.ok) {
        const data = await resp.json();
        setAtsJobs(data.jobs || []);
      }
    } catch {}
  }, [apiCall]);

  useEffect(() => {
    if (companyId && canManageCompany) {
      loadAtsConnections(companyId);
      loadAtsJobs(companyId);
    }
  }, [companyId, canManageCompany, loadAtsConnections, loadAtsJobs]);

  useEffect(() => {
    if (!companyId || !isOwner || !user?.userId) {
      setTeamMembers([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setTeamLoading(true);
      try {
        const resp = await apiCall(`${API_BASE}/api/v1/companies/${companyId}/team`);
        if (!resp.ok || cancelled) return;
        const data = await resp.json();
        if (!cancelled) setTeamMembers(data.members || []);
      } catch {
        if (!cancelled) setTeamMembers([]);
      } finally {
        if (!cancelled) setTeamLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, isOwner, user?.userId, apiCall]);

  const handleMemberRoleChange = async (targetUserId: string, role: 'admin' | 'member') => {
    if (!companyId || targetUserId === user?.userId) return;
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/companies/${companyId}/member-role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_user_id: targetUserId, role }),
      });
      if (resp.ok) {
        showToast(t('team.role_updated'), 'success');
        const tr = await apiCall(`${API_BASE}/api/v1/companies/${companyId}/team`);
        if (tr.ok) {
          const data = await tr.json();
          setTeamMembers(data.members || []);
        }
      } else {
        showToast(t('team.role_failed'), 'error');
      }
    } catch {
      showToast(t('team.role_failed'), 'error');
    }
  };

  const extractError = async (resp: Response, fallback: string): Promise<string> => {
    try {
      const body = await resp.json();
      const detail = body?.detail;
      if (typeof detail === 'string' && detail.trim()) return detail;
      if (detail && typeof detail === 'object' && typeof detail.error === 'string') return detail.error;
    } catch {}
    return fallback;
  };

  const handleAtsConnect = async () => {
    if (!companyId) return;
    let token: string;
    if (atsProvider === 'personio') {
      if (!atsToken.trim() || !atsPersonioCompanyId.trim()) return;
      const sub = atsPersonioSubdomain.trim();
      token = `${atsToken.trim()}||${atsPersonioCompanyId.trim()}||${sub}`;
    } else {
      token = atsToken.trim();
    }
    if (!token) return;
    setAtsConnecting(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/ats/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: companyId, provider: atsProvider, api_token: token }),
      });
      if (resp.ok) {
        showToast(t('ats.connection_success'), 'success');
        setAtsToken('');
        setAtsPersonioCompanyId('');
        setAtsPersonioSubdomain('');
        loadAtsConnections(companyId);
      } else {
        const msg = await extractError(resp, t('ats.connection_failed'));
        showToast(msg, 'error');
      }
    } catch {
      showToast(t('ats.connection_failed'), 'error');
    } finally {
      setAtsConnecting(false);
    }
  };

  const handleAtsDisconnect = async (provider: string) => {
    if (!companyId || !window.confirm(t('ats.disconnect_confirm'))) return;
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/ats/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: companyId, provider }),
      });
      if (resp.ok) {
        showToast(t('ats.disconnected'), 'success');
        loadAtsConnections(companyId);
        setAtsJobs([]);
      } else {
        const msg = await extractError(resp, t('ats.disconnect_failed'));
        showToast(msg, 'error');
      }
    } catch {
      showToast(t('ats.disconnect_failed'), 'error');
    }
  };

  const handleAtsSyncJobs = async (provider: string) => {
    if (!companyId) return;
    setAtsSyncing(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/ats/sync-jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: companyId, provider }),
      });
      if (resp.ok) {
        const data = await resp.json();
        showToast(t('ats.synced', { count: data.synced || 0 }), 'success');
        loadAtsJobs(companyId);
      } else {
        const msg = await extractError(resp, t('ats.sync_failed'));
        showToast(msg, 'error');
      }
    } catch {
      showToast(t('ats.sync_failed'), 'error');
    } finally {
      setAtsSyncing(false);
    }
  };

  const handleAtsAutoPush = async (provider: string, enabled: boolean) => {
    if (!companyId) return;
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/ats/auto-push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_id: companyId, provider, enabled }),
      });
      if (resp.ok) {
        showToast(enabled ? t('ats.auto_push_enabled') : t('ats.auto_push_disabled'), 'success');
        loadAtsConnections(companyId);
      } else {
        const msg = await extractError(resp, t('ats.auto_push_failed'));
        showToast(msg, 'error');
        loadAtsConnections(companyId);
      }
    } catch {
      showToast(t('ats.auto_push_failed'), 'error');
    }
  };

  // ── Save ──────────────────────────────────────────────────────

  const missingFields: string[] = [];
  if (!(logoUrl || logoPreview)) missingFields.push(t('toast.missing.logo'));
  if (!form.name.trim()) missingFields.push(t('toast.missing.name'));
  if (!form.bio.trim()) missingFields.push(t('toast.missing.bio'));
  if (!form.industry.trim()) missingFields.push(t('toast.missing.industry'));
  if (!form.size) missingFields.push(t('toast.missing.size'));
  if (!form.location.trim()) missingFields.push(t('toast.missing.location'));
  if (!form.website.trim()) missingFields.push(t('toast.missing.website'));
  if (!form.founded_year) missingFields.push(t('toast.missing.founded_year'));
  if (form.cevee_tags.length === 0) missingFields.push(t('toast.missing.cevee_tags'));
  const isFormComplete = missingFields.length === 0;

  const handleSave = async () => {
    if (!isFormComplete) { showToast(t('toast.fields_required'), 'error'); return; }
    setSaving(true);
    try {
      const normalizeSocial = (url: string) => {
        const t = url?.trim() || '';
        if (!t) return '';
        if (/^https?:\/\//i.test(t)) return t;
        return `https://${t.replace(/^\/+/, '')}`;
      };
      const payload = {
        ...form,
        tiktok_url: normalizeSocial(form.tiktok_url),
        instagram_url: normalizeSocial(form.instagram_url),
        linkedin_url: normalizeSocial(form.linkedin_url),
        founded_year: form.founded_year ? parseInt(form.founded_year) : null,
      };

      let resp;
      if (companyId) {
        resp = await apiCall(`${API_BASE}/api/v1/companies/${companyId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        resp = await apiCall(`${API_BASE}/api/v1/companies`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      const data = await resp.json();
      if (data.success && data.company) {
        const newId = data.company.id;
        setCompanyId(newId);
        setIsActive(data.company.is_active !== false);
        if (!companyId) {
          setIsOwner(true);
          setCanManageCompany(true);
          setCompanyRole('owner');
        }

        if (pendingLogo) {
          await uploadLogo(newId, pendingLogo);
          setPendingLogo(null);
          setLogoPreview(null);
        }

        const wasNew = !companyId;
        showToast(wasNew ? t('toast.created') : t('toast.updated'), 'success');
        setTimeout(() => navigate('/?tab=hire'), 300);
      } else {
        showToast(t('toast.save_failed'), 'error');
      }
    } catch (e: any) {
      showToast(e.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!companyId) return;
    setDeleting(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/companies/${companyId}`, { method: 'DELETE' });
      if (!resp.ok) {
        const err = await resp.json().catch(() => null);
        throw new Error(err?.detail || `Delete failed (${resp.status})`);
      }
      setCompanyId(null);
      setForm(emptyForm);
      setLogoUrl(null);
      setLogoPreview(null);
      setPendingLogo(null);
      setShowDeleteConfirm(false);
      setIsOwner(true);
      setCanManageCompany(true);
      setCompanyRole(null);
      setTeamMembers([]);
      showToast(t('toast.deleted'), 'success');
    } catch (e: any) {
      showToast(e.message || t('toast.delete_failed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ── Logo upload ───────────────────────────────────────────────

  const uploadLogo = async (targetCompanyId: string, file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await apiCall(`${API_BASE}/api/v1/companies/${targetCompanyId}/logo`, {
        method: 'POST',
        body: formData,
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => null);
        throw new Error(errData?.detail || `Upload failed (${resp.status})`);
      }
      const data = await resp.json();
      if (data.success) {
        setLogoUrl(data.logo_url);
        showToast(t('logo.uploaded'), 'success');
      }
    } catch (e: any) {
      console.error('Logo upload error:', e);
      showToast(e.message || t('logo.upload_failed'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const preview = URL.createObjectURL(file);
    setLogoPreview(preview);

    if (companyId) {
      await uploadLogo(companyId, file);
    } else {
      setPendingLogo(file);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────

  const toggleCeveeTag = (tag: string) => {
    setForm((f) => ({
      ...f,
      cevee_tags: f.cevee_tags.includes(tag)
        ? f.cevee_tags.filter((t) => t !== tag)
        : [...f.cevee_tags, tag],
    }));
  };

  const set = (field: keyof CompanyForm, value: string) => {
    if (!canManageCompany && companyId) return;
    setForm((f) => ({ ...f, [field]: value }));
  };
  const readOnly = !canManageCompany && !!companyId;

  // ── Render ────────────────────────────────────────────────────

  // Signed out (auth resolved, no session) → shared recruiter gate.
  if (!authLoading && !isSignedIn) {
    return <RecruiterLoggedOutGate returnTo="/company/manage" />;
  }

  if (loading) {
    return (
      <RecruiterPageShell contentClassName="max-w-2xl">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="p-4 sm:p-6 border-b border-slate-100 relative overflow-hidden">
              <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent z-10" />
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-slate-200 animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 sm:h-6 w-40 sm:w-52 bg-slate-200 rounded animate-pulse" />
                  <div className="h-3.5 w-28 sm:w-36 bg-slate-100 rounded animate-pulse" />
                </div>
              </div>
            </div>
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent z-10" />
                  <div className="h-3.5 sm:h-4 w-20 sm:w-28 bg-slate-200 rounded mb-2 animate-pulse" />
                  <div className="h-9 sm:h-10 w-full bg-slate-100 rounded-lg animate-pulse" />
                </div>
              ))}
              <div className="h-10 sm:h-12 w-full bg-slate-200 rounded-lg animate-pulse" />
            </div>
          </div>
      </RecruiterPageShell>
    );
  }

  if (!companyId && mode === 'choose') {
    return (
      <RecruiterPageShell contentClassName="max-w-lg">
          <div className="text-center mb-4 sm:mb-8">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-pink-50 flex items-center justify-center mx-auto mb-2 sm:mb-4">
              <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-pink-500" />
            </div>
            <h1 className="text-base sm:text-2xl font-bold text-slate-900 mb-1 sm:mb-2">{t('chooser.title')}</h1>
            <p className="text-sm text-slate-500">{t('chooser.subtitle')}</p>
          </div>
          <div className="space-y-2 sm:space-y-3">
            <button
              onClick={() => setMode('create')}
              className="w-full flex items-center gap-2 sm:gap-4 p-3 sm:p-5 bg-white rounded-xl shadow-sm border border-slate-200 hover:border-slate-300 transition-colors group"
            >
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-pink-50 flex items-center justify-center flex-shrink-0">
                <Plus className="w-4 h-4 sm:w-6 sm:h-6 text-pink-600" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <p className="font-semibold text-slate-900 text-sm sm:text-base">{t('chooser.create')}</p>
                <p className="text-xs text-slate-400">{t('chooser.create_desc')}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
            </button>

            <button
              onClick={() => setMode('join')}
              className="w-full flex items-center gap-2 sm:gap-4 p-3 sm:p-5 bg-white rounded-xl shadow-sm border border-slate-200 hover:border-slate-300 transition-colors group"
            >
              <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0">
                <Search className="w-4 h-4 sm:w-6 sm:h-6 text-violet-600" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <p className="font-semibold text-slate-900 text-sm sm:text-base">{t('chooser.join')}</p>
                <p className="text-xs text-slate-400">{t('chooser.join_desc')}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 transition-colors" />
            </button>
          </div>
      </RecruiterPageShell>
    );
  }

  if (!companyId && mode === 'join') {
    return (
      <RecruiterPageShell contentClassName="max-w-lg">
          <button onClick={() => setMode('choose')} className="text-sm text-slate-400 hover:text-slate-600 mb-4 flex items-center gap-1">
            &larr; {t('chooser.back')}
          </button>
          <div className="text-center mb-4">
            <h1 className="text-base sm:text-xl font-bold text-slate-900 mb-1">{t('chooser.join')}</h1>
            <p className="text-sm text-slate-500">{t('chooser.search_hint')}</p>
          </div>
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={searchQuery}
              onChange={(e) => handleSearchInput(e.target.value)}
              placeholder={t('chooser.search_placeholder')}
              className="w-full pl-10 pr-4 py-3 text-sm rounded-lg border border-slate-200 hover:border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-inset focus:ring-slate-900/10 outline-none"
              autoFocus
            />
            {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pink-400 animate-spin" />}
          </div>
          <div className="space-y-2">
            {searchResults.map((c: any) => (
              <div key={c.id} className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-colors">
                {c.logo_url ? (
                  <img src={`${API_BASE}/api/v1/companies/logos/${c.logo_url}`} alt="" className="w-10 h-10 rounded-lg object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-pink-50 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-pink-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-900 truncate">{c.name}</p>
                  <p className="text-xs text-slate-400 truncate">{[c.industry, c.location].filter(Boolean).join(' · ')}</p>
                </div>
                <button
                  onClick={() => handleJoin(c.id)}
                  disabled={joining}
                  className="px-4 py-2 text-xs font-semibold rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 transition-colors"
                >
                  {joining ? <Loader2 className="w-3 h-3 animate-spin" /> : t('chooser.join_btn')}
                </button>
              </div>
            ))}
            {searchQuery.trim().length >= 2 && !searching && searchResults.length === 0 && (
              <div className="text-center py-8">
                <p className="text-sm text-slate-400 mb-3">{t('chooser.not_found')}</p>
                <button
                  onClick={() => setMode('create')}
                  className="text-sm font-medium text-pink-500 hover:text-pink-600"
                >
                  {t('chooser.create_instead')}
                </button>
              </div>
            )}
          </div>
      </RecruiterPageShell>
    );
  }

  return (
    <RecruiterPageShell contentClassName={companyId ? 'max-w-5xl' : 'max-w-2xl'}>
        {!companyId && (
          <button onClick={() => setMode('choose')} className="text-sm text-slate-400 hover:text-slate-600 mb-3 flex items-center gap-1">
            &larr; {t('chooser.back')}
          </button>
        )}
        {/* Header */}
        <div className="mb-3 sm:mb-6">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              {companyId ? (
                form.name.trim() ? (
                  <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 truncate">{form.name}</h1>
                ) : null
              ) : (
                <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900">
                  {t('heading.new_company')}{' '}
                  <span className="text-pink-600">{t('heading.create_word')}</span>
                </h1>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {companyId && (
                <button
                  onClick={() => setShowExampleModal(true)}
                  className="hidden sm:flex text-xs font-medium text-slate-600 hover:text-slate-900 items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                >
                  <Eye className="w-3.5 h-3.5" />
                  {t('example.button')}
                </button>
              )}
            </div>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            {readOnly
              ? companyRole === 'admin'
                ? t('heading.admin_subtitle')
                : t('heading.member_subtitle')
              : !companyId
                ? t('heading.subtitle_create')
                : !isActive
                  ? t('heading.subtitle_pending')
                  : t('heading.subtitle')}
          </p>
          {!readOnly && companyId && !isActive && (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs sm:text-sm text-amber-800 leading-snug">
              {t('heading.review_hint')}{' '}
              <a
                href={RECRUITER_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold underline decoration-amber-400 hover:decoration-amber-600"
              >
                {t('heading.review_cta')}
              </a>
            </div>
          )}
          {!readOnly && !companyId && (
            <div className="mt-3 rounded-xl border border-pink-100 bg-pink-50/60 px-3 py-2 text-xs sm:text-sm text-slate-700 leading-snug">
              {t('heading.create_hint')}{' '}
              <a
                href={RECRUITER_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-pink-600 hover:text-pink-700 underline decoration-pink-300 hover:decoration-pink-500"
              >
                {t('heading.create_cta')}
              </a>
            </div>
          )}
        </div>

        {companyId && canManageCompany && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-slate-200 mb-4 sm:mb-6 overflow-hidden">
            <button
              onClick={() => setAtsExpanded(!atsExpanded)}
              className="w-full flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-violet-50 flex items-center justify-center">
                  <Plug className="w-4 h-4 sm:w-5 sm:h-5 text-violet-500" />
                </div>
                <div className="text-left">
                  <h2 className="text-sm sm:text-base font-semibold text-slate-900">{t('ats.title')}</h2>
                  <p className="text-[10px] sm:text-xs text-slate-400">{t('ats.subtitle')}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {atsConnections.length > 0 && (
                  <span className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                    {atsConnections.length} {t('ats.connected')}
                  </span>
                )}
                {atsExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </div>
            </button>

            <AnimatePresence>
              {atsExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-4 border-t border-slate-100">
                    {atsConnections.map((conn: any) => (
                      <div key={conn.id} className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 sm:p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" />
                            <span className="text-sm font-semibold text-slate-900 capitalize">{conn.provider}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{t('ats.connected')}</span>
                          </div>
                          <button
                            onClick={() => handleAtsDisconnect(conn.provider)}
                            className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                          >
                            {t('ats.disconnect')}
                          </button>
                        </div>

                        <div className="flex flex-wrap gap-2 mt-3">
                          <button
                            onClick={() => handleAtsSyncJobs(conn.provider)}
                            disabled={atsSyncing}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-violet-300 hover:bg-violet-50 transition-all disabled:opacity-50"
                          >
                            {atsSyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            {atsSyncing ? t('ats.syncing') : t('ats.sync_jobs')}
                          </button>

                          <label className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                            isPremiumRecruiter
                              ? 'bg-white border-slate-200 cursor-pointer hover:border-violet-300'
                              : 'bg-slate-50 border-slate-100 cursor-not-allowed opacity-60'
                          }`}>
                            <Zap className={`w-3 h-3 ${isPremiumRecruiter ? 'text-amber-500' : 'text-slate-300'}`} />
                            <span className="text-slate-600">{t('ats.auto_push')}</span>
                            {isPremiumRecruiter ? (
                              <input
                                type="checkbox"
                                checked={conn.auto_push}
                                onChange={(e) => handleAtsAutoPush(conn.provider, e.target.checked)}
                                className="ml-1 w-3.5 h-3.5 rounded text-violet-500 focus:ring-violet-300"
                              />
                            ) : (
                              <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-pink-100 text-pink-500 font-semibold">Premium</span>
                            )}
                          </label>
                        </div>

                        {atsJobs.length > 0 && (
                          <div className="mt-3 space-y-1">
                            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Jobs ({atsJobs.length})</p>
                            <div className="flex flex-wrap gap-1">
                              {atsJobs.slice(0, 8).map((job: any) => (
                                <span key={job.id} className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600">
                                  {job.title}
                                </span>
                              ))}
                              {atsJobs.length > 8 && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">
                                  +{atsJobs.length - 8}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {(atsConnections.length === 0 || !atsConnections.find((c: any) => c.provider === atsProvider)) && (
                      <div className="rounded-xl border border-dashed border-slate-200 p-3 sm:p-4">
                        <p className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{t('ats.provider_label')}</p>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {['ashby'].map((p) => (
                            <button
                              key={p}
                              onClick={() => setAtsProvider(p)}
                              disabled={atsConnections.some((c: any) => c.provider === p)}
                              className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors capitalize ${
                                atsProvider === p
                                  ? 'bg-violet-50 border-violet-300 text-violet-600'
                                  : atsConnections.some((c: any) => c.provider === p)
                                    ? 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed'
                                    : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                              }`}
                            >
                              {p}
                            </button>
                          ))}
                          {['personio', 'needle', 'workday'].map((p) => (
                            <span
                              key={p}
                              className="px-2.5 py-1 rounded-lg text-xs font-medium border border-slate-100 bg-slate-50 text-slate-300 capitalize cursor-default"
                              title={t('ats.coming_soon')}
                            >
                              {p} <span className="text-[9px] normal-case">soon</span>
                            </span>
                          ))}
                        </div>

                        <p className="text-[10px] text-slate-400 mb-2">
                          {t(`ats.token_help_${atsProvider}` as any)}
                        </p>

                        {atsProvider === 'personio' ? (
                          <div className="space-y-2">
                            <input
                              type="password"
                              value={atsToken}
                              onChange={(e) => setAtsToken(e.target.value)}
                              placeholder={t('ats.personio_token_placeholder')}
                              className="w-full px-3 py-2 text-xs rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-inset focus:ring-slate-900/10 outline-none"
                            />
                            <input
                              type="text"
                              value={atsPersonioCompanyId}
                              onChange={(e) => setAtsPersonioCompanyId(e.target.value)}
                              placeholder={t('ats.personio_company_id_placeholder')}
                              className="w-full px-3 py-2 text-xs rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-inset focus:ring-slate-900/10 outline-none"
                            />
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={atsPersonioSubdomain}
                                onChange={(e) => setAtsPersonioSubdomain(e.target.value)}
                                placeholder={t('ats.personio_subdomain_placeholder')}
                                className="flex-1 px-3 py-2 text-xs rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-inset focus:ring-slate-900/10 outline-none"
                              />
                              <button
                                onClick={handleAtsConnect}
                                disabled={atsConnecting || !atsToken.trim() || !atsPersonioCompanyId.trim()}
                                className="px-4 py-2 text-xs font-semibold rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                              >
                                {atsConnecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plug className="w-3 h-3" />}
                                {atsConnecting ? t('ats.connecting') : t('ats.connect')}
                              </button>
                            </div>
                            <p className="text-[9px] text-slate-400">{t('ats.personio_subdomain_hint')}</p>
                            <p className="text-[9px] text-slate-400">{t('ats.personio_company_id_hint')}</p>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <input
                              type="password"
                              value={atsToken}
                              onChange={(e) => setAtsToken(e.target.value)}
                              placeholder={t(`ats.${atsProvider}_token_placeholder` as any, { defaultValue: t('ats.token_placeholder') })}
                              className="flex-1 px-3 py-2 text-xs rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-inset focus:ring-slate-900/10 outline-none"
                            />
                            <button
                              onClick={handleAtsConnect}
                              disabled={atsConnecting || !atsToken.trim()}
                              className="px-4 py-2 text-xs font-semibold rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                            >
                              {atsConnecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plug className="w-3 h-3" />}
                              {atsConnecting ? t('ats.connecting') : t('ats.connect')}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        <div className={companyId && isOwner ? 'grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6' : ''}>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden ${companyId && isOwner ? 'lg:col-span-2' : ''}`}>
          {companyId && (
            <div className="px-4 sm:px-6 py-3 border-b border-slate-100">
              <h2 className="text-sm sm:text-base font-semibold text-slate-900">{t('form.sectionTitle', { defaultValue: 'Company profile' })}</h2>
              <p className="text-xs text-slate-500 mt-0.5">{t('heading.subtitle')}</p>
            </div>
          )}
          <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
          {/* Logo + Example */}
          <div className="flex items-center gap-2 sm:gap-4">
            {logoPreview ? (
              <img src={logoPreview} alt="Preview" className="w-12 h-12 sm:w-20 sm:h-20 rounded-lg sm:rounded-2xl object-cover shadow-sm border border-slate-100" />
            ) : logoUrl ? (
              <img src={`${API_BASE}/api/v1/companies/logos/${logoUrl}`} alt="Logo" className="w-12 h-12 sm:w-20 sm:h-20 rounded-lg sm:rounded-2xl object-cover shadow-sm border border-slate-100" />
            ) : (
              <div className="w-12 h-12 sm:w-20 sm:h-20 rounded-lg sm:rounded-2xl bg-pink-50 flex items-center justify-center">
                <Building2 className="w-6 h-6 sm:w-10 sm:h-10 text-pink-400" />
              </div>
            )}
            <div>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading || readOnly}
                className="flex items-center gap-1.5 sm:gap-2 px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded-lg bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200 disabled:opacity-50 transition-colors"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                <span className="sm:hidden">Logo *</span>
                <span className="hidden sm:inline">{logoUrl || logoPreview ? t('logo.change') : t('logo.upload')}</span>
              </button>
              {pendingLogo && !companyId && <p className="text-xs text-pink-500 mt-1">{t('logo.pending')}</p>}
            </div>
            <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={handleLogoSelect} className="hidden" />
            {!companyId && (
            <button
              onClick={() => setShowExampleModal(true)}
              className="ml-auto text-xs sm:text-sm text-pink-500 hover:text-pink-600 font-medium flex items-center gap-1 transition-colors border border-pink-200 rounded-lg px-2 py-1 sm:px-3 sm:py-1.5 hover:bg-pink-50 shrink-0"
            >
              <Eye className="w-3.5 h-3.5" />
              {t('example.button')}
            </button>
            )}
          </div>

          {/* Name + Industry */}
          <div className="grid grid-cols-2 gap-2 sm:gap-4">
            <div>
              <label className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('labels.name')}</label>
              <input value={form.name} onChange={(e) => set('name', e.target.value)}
                className="mt-0.5 sm:mt-1 w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-slate-900/10 outline-none" />
            </div>
            <div>
              <label className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('labels.industry')}</label>
              <input value={form.industry} onChange={(e) => set('industry', e.target.value)} placeholder={t('placeholders.industry')}
                className="mt-0.5 sm:mt-1 w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-slate-900/10 outline-none" />
            </div>
          </div>

          {/* Location + Size */}
          <div className="grid grid-cols-2 gap-2 sm:gap-4">
            <div>
              <label className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><MapPin className="w-3 h-3" /> {t('labels.location')}</label>
              <input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder={t('placeholders.location')}
                className="mt-0.5 sm:mt-1 w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-slate-900/10 outline-none" />
            </div>
            <div>
              <label className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Users className="w-3 h-3" /> {t('labels.team_size')}</label>
              <div className="mt-1 flex flex-wrap gap-1">
                {SIZE_OPTIONS.map((s) => (
                  <button key={s} onClick={() => set('size', form.size === s ? '' : s)}
                    className={`px-1.5 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium border transition-colors ${
                      form.size === s ? 'bg-pink-50 border-pink-300 text-pink-600' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}>{s}</button>
                ))}
              </div>
            </div>
          </div>

          {companyId && <div className="border-t border-slate-100 pt-4 sm:pt-6" />}

          {/* Website + Founded */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
            <div className="col-span-2 sm:col-span-1">
              <label className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Globe className="w-3 h-3" /> {t('labels.website')}</label>
              <input value={form.website} onChange={(e) => set('website', e.target.value)} placeholder={t('placeholders.website')}
                onFocus={(e) => { if (!e.target.value) set('website', 'https://'); }}
                className="mt-0.5 sm:mt-1 w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-slate-900/10 outline-none" />
            </div>
            <div>
              <label className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Calendar className="w-3 h-3" /> {t('labels.founded_year')}</label>
              <input value={form.founded_year} onChange={(e) => set('founded_year', e.target.value)} placeholder={t('placeholders.founded_year')} type="number" min="0"
                className="mt-0.5 sm:mt-1 w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-slate-900/10 outline-none" />
            </div>
          </div>

          {/* Social Links (optional) */}
          <div>
            <label className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">
              {t('labels.social_links', { defaultValue: 'Social Links (optional)' })}
            </label>
            <div className="mt-0.5 sm:mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
              <div className="relative">
                <TikTokIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input value={form.tiktok_url} onChange={(e) => set('tiktok_url', e.target.value)} placeholder="tiktok.com/@..."
                  className="w-full pl-7 pr-2 py-1 sm:py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-slate-900/10 outline-none" />
              </div>
              <div className="relative">
                <Instagram className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input value={form.instagram_url} onChange={(e) => set('instagram_url', e.target.value)} placeholder="instagram.com/..."
                  className="w-full pl-7 pr-2 py-1 sm:py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-slate-900/10 outline-none" />
              </div>
              <div className="relative">
                <Linkedin className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input value={form.linkedin_url} onChange={(e) => set('linkedin_url', e.target.value)} placeholder="linkedin.com/company/..."
                  className="w-full pl-7 pr-2 py-1 sm:py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-slate-900/10 outline-none" />
              </div>
            </div>
          </div>

          {companyId && <div className="border-t border-slate-100 pt-4 sm:pt-6" />}

          {/* Bio */}
          <div>
            <label className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('labels.about')}</label>
            <textarea value={form.bio} onChange={(e) => set('bio', e.target.value)} rows={3} placeholder={t('placeholders.about')}
              className="mt-0.5 sm:mt-1 w-full px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-slate-900/10 outline-none resize-none" />
          </div>

          {/* Looking for */}
          <div>
            <label className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('labels.looking_for')}</label>
            <input value={form.looking_for} onChange={(e) => set('looking_for', e.target.value)} placeholder={t('placeholders.looking_for')}
              className="mt-0.5 sm:mt-1 w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-slate-900/10 outline-none" />
          </div>

          {/* Cevee Tags (predefined + custom) */}
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <label className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <span className="bg-gradient-to-r from-pink-500 to-pink-600 bg-clip-text text-transparent">✦</span> {t('vibes.label')}
              </label>
              <button
                onClick={() => setCeveeTagsPreview(shuffleAndPick(CEVEE_TAGS_POOL, 10))}
                className="p-1 rounded-lg text-pink-400 hover:text-pink-600 hover:bg-pink-100 border border-pink-200 transition-all"
                title={t('vibes.refresh_title')}
              >
                <RefreshCw className="w-3 h-3" />
              </button>
            </div>
            <p className="text-[10px] sm:text-xs text-slate-400 mb-1 sm:mb-2">{t('vibes.description')}</p>
            <div className="flex flex-wrap gap-1">
              {(() => {
                const selected = new Set(form.cevee_tags);
                const customTags = form.cevee_tags.filter((ct) => !CEVEE_TAGS_POOL.includes(ct));
                const selectedPoolTags = form.cevee_tags.filter((ct) => CEVEE_TAGS_POOL.includes(ct));
                const previewFiltered = ceveeTagsPreview.filter((t) => !selected.has(t));
                return [...customTags, ...selectedPoolTags, ...previewFiltered];
              })().map((tag) => (
                <button key={tag} onClick={() => {
                  if (form.cevee_tags.length >= 5 && !form.cevee_tags.includes(tag)) return;
                  toggleCeveeTag(tag);
                }}
                  className={`px-1.5 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium border transition-colors ${
                    form.cevee_tags.includes(tag)
                      ? 'bg-gradient-to-r from-pink-50 to-rose-50 border-pink-400 text-pink-600'
                      : form.cevee_tags.length >= 5
                        ? 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed'
                        : 'bg-white border-slate-200 text-slate-500 hover:border-pink-300'
                  }`}>
                  {form.cevee_tags.includes(tag) && <Check className="w-3 h-3 inline mr-1" />}
                  {translateTag(tag, language, CEVEE_TAGS_EN)}
                </button>
              ))}
            </div>
            {form.cevee_tags.length < 5 && (
              <div className="flex gap-2 mt-2">
                <input
                  value={customVibeInput}
                  onChange={(e) => setCustomVibeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = customVibeInput.trim();
                      if (val && form.cevee_tags.length < 5 && !form.cevee_tags.includes(val)) {
                        toggleCeveeTag(val);
                        setCustomVibeInput('');
                      }
                    }
                  }}
                  placeholder="+ Custom vibe..."
                  className="flex-1 px-2 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs rounded-lg border border-slate-300 focus:border-slate-300 focus:ring-1 focus:ring-slate-900/10 outline-none"
                />
                <button
                  onClick={() => {
                    const val = customVibeInput.trim();
                    if (val && form.cevee_tags.length < 5 && !form.cevee_tags.includes(val)) {
                      toggleCeveeTag(val);
                      setCustomVibeInput('');
                    }
                  }}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-pink-50 text-pink-600 hover:bg-pink-100 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Live Preview — create flow only */}
          {!companyId && form.name.trim() && (
            <div>
              <p className="text-[9px] sm:text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5 sm:mb-2">{t('preview.title')}</p>
              <div className="bg-white rounded-xl shadow-md overflow-hidden border border-slate-200">
                <div className="px-3 sm:px-4 pt-3 sm:pt-4 pb-2 flex items-start gap-2 sm:gap-3">
                  {(logoPreview || logoUrl) ? (
                    <img
                      src={logoPreview || `${API_BASE}/api/v1/companies/logos/${logoUrl}`}
                      alt={form.name}
                      className="w-12 h-12 rounded-lg object-cover shadow-sm border border-slate-100"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-pink-100 to-pink-200 flex items-center justify-center shadow-sm">
                      <Building2 className="w-6 h-6 text-pink-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-900 text-sm truncate">{form.name}</h3>
                    {form.industry && <p className="text-[10px] text-slate-500 mt-0.5">{form.industry}</p>}
                    <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-[10px] text-slate-400">
                      {form.location && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" /> {form.location}</span>}
                      {form.size && <span className="flex items-center gap-0.5"><Users className="w-2.5 h-2.5" /> {form.size}</span>}
                      {form.founded_year && <span className="flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5" /> {form.founded_year}</span>}
                    </div>
                    {form.cevee_tags.length > 0 && (
                      <div className="flex flex-wrap gap-0.5 mt-1">
                        {form.cevee_tags.slice(0, 5).map((tag) => (
                          <span key={tag} className="text-[8px] px-1 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                            {translateTag(tag, language, CEVEE_TAGS_EN)}
                          </span>
                        ))}
                        {form.cevee_tags.length > 5 && (
                          <span className="text-[8px] px-1 py-0.5 rounded-full bg-slate-100 text-slate-400">+{form.cevee_tags.length - 5}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {form.bio && (
                  <div className="px-4">
                    <h4 className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{t('preview.about')}</h4>
                    <p className={`text-xs text-slate-600 leading-relaxed whitespace-pre-line ${!previewBioExpanded ? 'line-clamp-3' : ''}`}>{form.bio}</p>
                    {(form.bio.length > 120 || (form.bio.match(/\n/g) || []).length >= 3) && (
                      <button onClick={() => setPreviewBioExpanded(!previewBioExpanded)} className="text-[10px] text-pink-500 hover:text-pink-600 font-medium mt-0.5">
                        {previewBioExpanded ? t('preview.read_less') : t('preview.read_more')}
                      </button>
                    )}
                  </div>
                )}
                {form.looking_for && (
                  <div className="px-4 pt-1.5">
                    <h4 className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{t('preview.looking_for')}</h4>
                    <p className={`text-xs text-slate-600 leading-relaxed whitespace-pre-line ${!previewLookingForExpanded ? 'line-clamp-2' : ''}`}>{form.looking_for}</p>
                    {(form.looking_for.length > 80 || (form.looking_for.match(/\n/g) || []).length >= 2) && (
                      <button onClick={() => setPreviewLookingForExpanded(!previewLookingForExpanded)} className="text-[10px] text-pink-500 hover:text-pink-600 font-medium mt-0.5">
                        {previewLookingForExpanded ? t('preview.read_less') : t('preview.read_more')}
                      </button>
                    )}
                  </div>
                )}
                {form.website && (
                  <div className="px-4 pt-1.5">
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-400">
                      <Globe className="w-2.5 h-2.5" />
                      {form.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                    </span>
                  </div>
                )}
                <div className="px-4 py-3 flex items-center">
                  <span className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-slate-50 text-slate-400 border-2 border-slate-200 cursor-default">
                    <Heart className="w-3 h-3" /> {t('jobRadarAi:interested')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Save - sticky on mobile */}
          <div className="sticky bottom-0 -mx-3 sm:-mx-6 px-3 sm:px-6 py-2 sm:py-0 sm:relative sm:mx-0 sm:px-0 bg-white sm:bg-transparent border-t sm:border-t-0 border-slate-100 z-10">
            <button
              onClick={handleSave}
              disabled={saving || !isFormComplete || readOnly}
              className="w-full py-2 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold bg-slate-900 text-white shadow-sm hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5 sm:gap-2"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" /> : <Save className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
              {companyId ? t('actions.save') : t('actions.create')}
            </button>
            {!isFormComplete && !readOnly && (
              <p className="text-[10px] sm:text-xs text-pink-500 text-center mt-1.5">
                {t('toast.missing_fields')} {missingFields.join(', ')}
              </p>
            )}
          </div>

          {/* Delete - owner or company admin */}
          {companyId && canManageCompany && (
            <div className="pt-2 sm:pt-6 border-t border-slate-200 mt-2 sm:mt-6">
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full py-1.5 sm:py-2.5 rounded-lg sm:rounded-xl text-[10px] sm:text-xs font-medium text-slate-400 hover:text-red-500 hover:bg-red-50 border border-transparent hover:border-red-200 transition-all flex items-center justify-center gap-1.5"
                >
                  <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  {t('actions.delete')}
                </button>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 sm:p-4 space-y-3">
                  <p className="text-sm text-red-700 font-medium text-center">
                    {t('actions.delete_warning')}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 py-2 rounded-lg text-sm font-medium bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
                    >
                      {t('actions.cancel')}
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="flex-1 py-2 rounded-lg text-sm font-semibold bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-all flex items-center justify-center gap-1.5"
                    >
                      {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      {t('actions.delete_confirm')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Leave - only for non-managing members */}
          {companyId && !canManageCompany && (
            <div className="pt-4 sm:pt-6 border-t border-slate-200 mt-4 sm:mt-6">
              <button
                onClick={handleLeave}
                disabled={leaving}
                className="w-full py-2.5 rounded-xl text-xs font-medium text-slate-400 hover:text-red-500 hover:bg-red-50 border border-transparent hover:border-red-200 transition-all flex items-center justify-center gap-1.5"
              >
                {leaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                {t('actions.leave')}
              </button>
            </div>
          )}
          </div>
        </motion.div>

        {/* Team roles (owner only) — sidebar on desktop */}
        {companyId && isOwner && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-xl shadow-sm p-3 sm:p-6 border border-slate-200 overflow-hidden h-fit">
            <div className="pb-3 mb-3 border-b border-slate-100">
              <h3 className="text-sm sm:text-base font-semibold text-slate-900">{t('team.title')}</h3>
              <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5">{t('team.role_hint')}</p>
            </div>
            {teamLoading ? (
              <p className="text-xs text-slate-400 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />{t('team.loading')}</p>
            ) : (
              <ul className="space-y-2">
                {teamMembers.map((m) => (
                  <li key={m.id} className="flex flex-wrap items-center gap-2 text-xs sm:text-sm border border-slate-100 rounded-lg px-2 py-2 sm:px-3">
                    <span className="font-medium text-slate-800 truncate flex-1 min-w-0">{m.name}</span>
                    <span className="text-slate-400 truncate max-w-[40%] sm:max-w-none">{m.email}</span>
                    {m.company_role === 'owner' ? (
                      <span className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 ml-auto">
                        {t('team.role_owner')}
                      </span>
                    ) : m.id === user?.userId ? (
                      <span className="text-[10px] sm:text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 ml-auto">
                        {m.company_role === 'admin' ? t('team.role_admin') : t('team.role_member')}
                      </span>
                    ) : (
                      <select
                        value={m.company_role === 'admin' ? 'admin' : 'member'}
                        onChange={(e) => handleMemberRoleChange(m.id, e.target.value as 'admin' | 'member')}
                        className="ml-auto text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white"
                      >
                        <option value="member">{t('team.role_member')}</option>
                        <option value="admin">{t('team.role_admin')}</option>
                      </select>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        )}
        </div>

      {/* Example Modal */}
      <AnimatePresence>
        {showExampleModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4"
            onClick={() => setShowExampleModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-50 sm:rounded-2xl rounded-t-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-slate-50 sm:rounded-t-2xl z-10 px-4 pt-4 pb-2 flex items-center justify-between border-b border-slate-200">
                <h3 className="text-sm sm:text-base font-semibold text-slate-900">{t('example.modal_title')}</h3>
                <button
                  onClick={() => setShowExampleModal(false)}
                  className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-4">
                <CompanyCard
                  company={{
                    name: 'Cevee',
                    bio: 'Cevee ist die Karriereplattform für Gen Z 🌟 Wir helfen Studis, Azubis & Young Professionals ihre Karriere zu entdecken - bevor der Druck entsteht, sich entscheiden zu müssen. Bei uns könnt ihr echte Karrierewege explorieren, andere mit eurer Geschichte inspirieren und von Unternehmen gefunden werden, die wirklich zu euch passen!',
                    industry: 'EdTech & Software',
                    location: 'Remote, Koblenz',
                    size: '1-10',
                    website: 'https://cevee.eu',
                    founded_year: 2025,
                    cevee_tags: ['Purpose-driven', 'Built different', 'Nahbar & echt', 'Safe Space', 'Real Talk Kultur'],
                    looking_for: 'Founding AI Engineers, Marketing und Software Engineering Werkis & Interns',
                    logo_url: 'logo-e1231566-3a7b-4efe-ba03-1fae1d35eb38.webp',
                  }}
                  showActions={true}
                  translateTag={(tag) => translateTag(tag, language, CEVEE_TAGS_EN)}
                  aboutLabel={t('preview.about')}
                  lookingForLabel={t('preview.looking_for')}
                  interestedLabel={t('preview.interested')}
                  readMoreLabel={t('preview.read_more')}
                  readLessLabel={t('preview.read_less')}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </RecruiterPageShell>
  );
}
